//src\screens\ValidationScreen.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  TextInput,
  TouchableOpacity,
  RefreshControl,
  Alert,
  Platform,
} from 'react-native';
import {
  Appbar,
  Card,
  Title,
  Paragraph,
  Button,
  Text,
  Chip,
  Badge,
  Divider,
  Searchbar,
  Menu,
  IconButton,
  ActivityIndicator,
} from 'react-native-paper';
import { getFormData } from '../data/FormsData';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { AVAQualisTheme } from '../theme/theme';
import {
  getResponsesPendingValidation,
  getValidatedResponses,
  validateResponse,
  getFormById,
  checkServerHealth,
} from '../lib/api';
import AuthService from '../services/authService';
import StorageManager from '../utils/StorageManager';
import { styles } from './ValidationScreen.css';
import { getUnidadeHierarchy } from '../data/EstruturaOrganizacional';

export default function ValidationScreen({ onBack, onEditResponse }) {
  const [pendingResponses, setPendingResponses] = useState([]);
  const [validatedResponses, setValidatedResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date' ou 'name'
  const [menuVisible, setMenuVisible] = useState(false);
  const [serverOnline, setServerOnline] = useState(true);
  const [validatingId, setValidatingId] = useState(null);
  const [viewedResponses, setViewedResponses] = useState(new Set());
  const [viewedForRevalidation, setViewedForRevalidation] = useState(new Set());

  const currentUser = AuthService.getAvaliador();

  // Carregar respostas
  const loadResponses = useCallback(async () => {
    try {
      setLoading(true);

      // Verificar conexão
      const health = await checkServerHealth();
      setServerOnline(health.status === 'online');

      if (health.status !== 'online') {
        Alert.alert(
          '⚠️ Servidor Offline',
          'Não é possível acessar a validação sem conexão com o servidor.'
        );
        return;
      }

      // Buscar respostas pendentes e validadas
      const [pending, validated] = await Promise.all([
        getResponsesPendingValidation(),
        getValidatedResponses(),
      ]);

      // Enriquecer com dados do formulário
      const enrichResponse = async response => {
        try {
          const form = await getFormById(response.formId);
          return {
            ...response,
            formData: form,
            daysSinceCompletion: Math.floor(
              (new Date() - new Date(response.completedAt)) / (1000 * 60 * 60 * 24)
            ),
          };
        } catch (error) {
          console.error('Erro ao buscar dados do formulário:', error);
          return response;
        }
      };

      const enrichedPending = await Promise.all(pending.map(enrichResponse));
      const enrichedValidated = await Promise.all(validated.map(enrichResponse));
      console.log('📊 Respostas validadas carregadas:', {
        total: enrichedValidated.length,
        comValidacao: enrichedValidated.filter(r => r.validacao?.validadoPor).length,
      });

      setPendingResponses(enrichedPending);
      setValidatedResponses(enrichedValidated);
    } catch (error) {
      console.error('Erro ao carregar respostas:', error);
      Alert.alert('Erro', 'Não foi possível carregar as respostas.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadResponses();
  }, [loadResponses]);

  // Filtrar respostas
  const filterResponses = responses => {
    if (!searchQuery) return responses;

    const query = searchQuery.toLowerCase();
    return responses.filter(response => {
      const formTitle = response.formTitle?.toLowerCase() || '';
      const avaliadorNome = response.avaliador?.nome?.toLowerCase() || '';
      const processo =
        response.formData?.metadata?.processosAvaliados?.[0]?.nome?.toLowerCase() || '';
      const unidades =
        response.formData?.metadata?.unidadesOrganizacionais
          ?.map(u => u.nome.toLowerCase())
          .join(' ') || '';

      return (
        formTitle.includes(query) ||
        avaliadorNome.includes(query) ||
        processo.includes(query) ||
        unidades.includes(query)
      );
    });
  };

  // Ordenar respostas
  const sortResponses = responses => {
    const sorted = [...responses];

    if (sortBy === 'date') {
      sorted.sort((a, b) => {
        const dateA = new Date(a.validacao?.dataValidacao || a.completedAt || new Date());
        const dateB = new Date(b.validacao?.dataValidacao || b.completedAt || new Date());
        return dateB - dateA;
      });
    } else if (sortBy === 'name') {
      sorted.sort((a, b) => (a.formTitle || '').localeCompare(b.formTitle || ''));
    }

    return sorted;
  };

  // Validar resposta
 const handleValidate = async response => {
    try {
      setValidatingId(response.id);

      const validador = {
        id: currentUser.id,
        nome: currentUser.nome,
        login: currentUser.email || currentUser.login,
        level: currentUser.level,
      };

      // Validar no servidor
      const validatedResponse = await validateResponse(response.id, validador);

      // IMPORTANTE: Atualizar também no StorageManager local
      try {
        const responses = await StorageManager.loadFormResponses();
        const index = responses.findIndex(r => r.id === response.id);
        
        if (index >= 0) {
          responses[index] = {
            ...responses[index],
            validacao: validatedResponse.validacao,
            synced: true,
            syncedAt: new Date().toISOString()
          };
          
          await StorageManager.saveFormResponses(responses);
          console.log('✅ Validação salva localmente no StorageManager');
        }
      } catch (localError) {
        console.error('⚠️ Erro ao salvar validação localmente:', localError);
      }

      Alert.alert('✅ Sucesso', 'Resposta validada com sucesso!', [
        { text: 'OK', onPress: () => loadResponses() },
      ]);
    } catch (error) {
      console.error('Erro ao validar:', error);
      Alert.alert('❌ Erro', 'Não foi possível validar a resposta.');
    } finally {
      setValidatingId(null);
    }
  };
  // Formatar data
  const formatDate = dateString => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Obter complexidade do formulário
  const getFormComplexity = questionCount => {
    if (!questionCount || isNaN(questionCount)) {
      return { label: 'N/A', color: AVAQualisTheme.colors.tertiary, icon: '❓' };
    }

    if (questionCount <= 8)
      return { label: 'Rápido', color: AVAQualisTheme.colors.success, icon: '⚡' };
    if (questionCount <= 15)
      return { label: 'Médio', color: AVAQualisTheme.colors.warning, icon: '⏱️' };
    return { label: 'Detalhado', color: AVAQualisTheme.colors.error, icon: '📋' };
  };

  // Contar requisitos essenciais
  const countEssentialRequirements = form => {
    if (!form || !form.requirements || !Array.isArray(form.requirements)) {
      if (form && form.questions && Array.isArray(form.questions)) {
        return form.questions.filter(q => q.isEssential).length;
      }
      return 0;
    }
    return form.requirements.filter(req => req.isEssential).length;
  };

  // Componente de tooltip/alerta
  const ValidationAlert = ({ visible, message }) => {
    if (!visible) return null;

    return (
      <View style={styles.validationAlert}>
        <MaterialCommunityIcons name="alert-circle" size={16} color="#FF9800" />
        <Text style={styles.validationAlertText}>{message}</Text>
      </View>
    );
  };

  // Componente de instruções
  const ValidationInstructions = ({ isValidated = false }) => {
    if (isValidated) {
      return (
        <Card style={[styles.instructionCard, styles.instructionCardValidated]}>
          <View style={styles.instructionContent}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <MaterialCommunityIcons
                name="information-outline"
                size={16}
                color="#757575"
                style={styles.instructionIcon}
              />
              <Text style={[styles.instructionTitle, styles.instructionTitleValidated]}>
                Para Revalidar
              </Text>
            </View>
            <Text style={[styles.instructionText, styles.instructionTextValidated]}>
              Clique em "Revisar e Validar" → Verifique respostas/anexos → Role até o final → Clique
              em "Revalidar"
            </Text>
          </View>
        </Card>
      );
    }

    return (
      <Card style={styles.instructionCard}>
        <View style={styles.instructionContent}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <MaterialCommunityIcons
              name="alert-circle"
              size={20}
              color="#1976D2"
              style={styles.instructionIcon}
            />
            <Text style={styles.instructionTitle}>Como Validar</Text>
          </View>
          <Text style={styles.instructionText}>
            1. Clique no botão "Revisar e Validar"{'\n'}
            2. Verifique todas as respostas, descrições e anexos{'\n'}
            3. Role até o final da página{'\n'}
            4. Clique no botão "Validar Formulário"
          </Text>
        </View>
      </Card>
    );
  };

  // Renderizar card de resposta
  const renderResponseCard = (response, isPending = true) => {
    const isValidating = validatingId === response.id;
    const complexity = response.formData
      ? getFormComplexity(response.formData.totalQuestions)
      : null;
    const essentialCount = response.formData ? countEssentialRequirements(response.formData) : 0;

    return (
      <Card
        key={response.id}
        style={[styles.responseCard, isPending ? styles.pendingCard : styles.validatedCard]}
      >
        <Card.Content>
          {/* Cabeçalho */}
          <View style={styles.cardHeader}>
            <View style={styles.titleSection}>
              <Title style={styles.cardTitle}>{response.formTitle}</Title>
              {/* Descrição logo abaixo do título */}
              {response.formData?.description && (
                <Text style={styles.formDescription} numberOfLines={2}>
                  {response.formData.description}
                </Text>
              )}
              <Text style={styles.avaliadorText}>👤 Avaliador: {response.avaliador?.nome}</Text>
            </View>

            {isPending ? (
              <Badge style={[styles.badge, styles.pendingBadge]} size={28}>
                ⏳ {response.daysSinceCompletion}d
              </Badge>
            ) : (
              <MaterialCommunityIcons name="check-decagram" size={32} color="#4CAF50" />
            )}
          </View>

          {/* Informações do formulário */}
          {response.formData && (
            <View style={styles.formInfo}>
              {/* Linha de metadados compacta */}
              <View style={styles.metaInfoRow}>
                <Text style={styles.versionText}>📏 v{response.formData.version}</Text>
                {complexity && (
                  <>
                    <Text style={styles.separator}>•</Text>
                    <Text style={[styles.complexityText, { color: complexity.color }]}>
                      {complexity.icon} {complexity.label}
                    </Text>
                  </>
                )}
                {essentialCount > 0 && (
                  <>
                    <Text style={styles.separator}>•</Text>
                    <Text style={[styles.complexityText, { color: '#FFC107', fontWeight: 'bold' }]}>
                      ⚠️ {essentialCount} RE
                    </Text>
                  </>
                )}
                <Text style={styles.separator}>•</Text>
                <Text style={styles.complexityText}>
                  📋 {response.formData.totalQuestions || 0} questões
                </Text>
              </View>

              {/* Processos avaliados inline */}
              {response.formData.metadata?.processosAvaliados?.length > 0 && (
                <View style={styles.processosInlineContainer}>
                  <Text style={styles.processosLabel}>
                    ⚙️ Processos ({response.formData.metadata.processosAvaliados.length}):
                  </Text>
                  <View style={styles.processosChipsRow}>
                    {response.formData.metadata.processosAvaliados.map(processo => (
                      <Chip
                        key={processo.id}
                        icon="folder"
                        style={styles.processoChip}
                        textStyle={styles.chipText}
                        compact
                      >
                        {processo.nome}
                      </Chip>
                    ))}
                  </View>
                </View>
              )}

              {/* Informações da avaliação inline */}
              {response.formData.metadata && (
                <View style={styles.evaluationInfoInline}>
                  {response.formData.metadata.dataAvaliacao && (
                    <Text style={styles.evaluationTextInline}>
                      📅 {response.formData.metadata.dataAvaliacao}
                    </Text>
                  )}
                  {response.formData.metadata.horarioAvaliacao && (
                    <Text style={styles.evaluationTextInline}>
                      ⏰ {response.formData.metadata.horarioAvaliacao}
                    </Text>
                  )}
                  {response.formData.metadata.localAvaliacao && (
                    <Text style={styles.evaluationTextInline}>
                      📍 {response.formData.metadata.localAvaliacao}
                    </Text>
                  )}
                  {response.formData.metadata.contatoAvaliacao && (
                    <Text style={styles.evaluationTextInline}>
                      📞 {response.formData.metadata.contatoAvaliacao}
                    </Text>
                  )}
                </View>
              )}

              {/* Emails das lideranças inline */}
              {response.formData.metadata?.emailsLiderancas?.length > 0 && (
                <View style={styles.emailsInlineContainer}>
                  <Text style={styles.emailsLabel}>
                    📧 Emails ({response.formData.metadata.emailsLiderancas.length}):
                  </Text>
                  <Text style={styles.emailsList} numberOfLines={1}>
                    {response.formData.metadata.emailsLiderancas.join(', ')}
                  </Text>
                </View>
              )}

              {/* Unidades organizacionais inline */}
              {response.formData.metadata?.unidadesOrganizacionais?.length > 0 && (
                <View style={styles.unidadesContainer}>
                  <Text style={styles.unidadesTitle}>
                    🏢 Unidades ({response.formData.metadata.unidadesOrganizacionais.length}):
                  </Text>
                  <View style={styles.unidadesInlineGrid}>
                    {response.formData.metadata.unidadesOrganizacionais.map((unidade, index) => (
                      <View key={unidade.id} style={styles.unidadeInlineCard}>
                        <Text style={styles.unidadeInlineCode}>{unidade.codigo}</Text>
                        <Text style={styles.unidadeInlineName} numberOfLines={1}>
                          {unidade.nome}
                        </Text>
                        <Text style={styles.unidadeInlineInfo}>
                          📞 {unidade.telefone} • 🏗️ {getUnidadeHierarchy(unidade.id)}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </View>
          )}

          {/* Datas */}
          <View style={styles.dateSection}>
            <Text style={styles.dateText}>📅 Finalizado: {formatDate(response.completedAt)}</Text>

            {!isPending && response.validacao && (
              <>
                <Text style={styles.validationText}>
                  ✅ Validado por: {response.validacao.validadoPor?.nome || 'Não identificado'}
                </Text>
                <Text style={styles.dateText}>
                  📅 Em:{' '}
                  {response.validacao.dataValidacao
                    ? formatDate(response.validacao.dataValidacao)
                    : 'Data não disponível'}
                </Text>

                {response.validacao.revalidacoes?.length > 0 && (
                  <Text style={styles.revalidationText}>
                    🔄 {response.validacao.revalidacoes.length} revalidação(ões)
                  </Text>
                )}
              </>
            )}

            {isPending && (
              <Text style={styles.waitingText}>
                ⏳ Aguardando validação há {response.daysSinceCompletion} dia(s)
              </Text>
            )}
          </View>
        </Card.Content>

        <Card.Actions>
          <Button
            mode="outlined"
            onPress={async () => {
              // Marcar como visualizado
              if (isPending) {
                setViewedResponses(prev => new Set([...prev, response.id]));
              } else {
                setViewedForRevalidation(prev => new Set([...prev, response.id]));
              }

              try {
                // IMPORTANTE: Verificar se temos todos os dados necessários
                if (!response.formId) {
                  console.error('❌ Resposta sem formId:', response);
                  Alert.alert('Erro', 'ID do formulário não encontrado na resposta');
                  return;
                }

                console.log('📋 Carregando dados para validação:', {
                  formId: response.formId,
                  responseId: response.id,
                  avaliador: response.avaliador?.nome,
                  respostasExistentes: Object.keys(response.answers || {}).length,
                  anexosExistentes: Object.keys(response.attachments || {}).length,
                });

                // Buscar o formulário completo primeiro
                let formData = null;

                // Se já temos formData na resposta, usar ela
                if (response.formData) {
                  formData = response.formData;
                  console.log('✅ Usando formData já existente na resposta');
                } else {
                  // Caso contrário, buscar do servidor
                  console.log('🔄 Buscando formulário do servidor...');
                  formData = await getFormById(response.formId);
                }

                if (!formData || !formData.id) {
                  console.error('❌ Formulário não encontrado ou inválido');
                  Alert.alert('Erro', 'Formulário não encontrado');
                  return;
                }

                // CORREÇÃO CRÍTICA: Garantir que temos o ID e os dados corretos
                const enrichedForm = {
                  // IMPORTANTE: Passar o formData completo
                  formData: formData,

                  // Dados básicos do formulário
                  id: formData.id || response.formId,
                  title: formData.title || response.formTitle,
                  description: formData.description,
                  version: formData.version,
                  totalQuestions: formData.totalQuestions || formData.questions?.length || 0,

                  // CRÍTICO: Copiar sections, questions e requirements
                  sections: formData.sections || [],
                  questions: formData.questions || [],
                  requirements: formData.requirements || [],
                  metadata: formData.metadata || {},

                  // Flags de modo
                  isValidationMode: true,
                  isEditingFromValidation: true,
                  isEditingFromResults: true,
                  skipInitialLoad: true,
                  hasExistingResponses: true,

                  // Dados da resposta existente
                  existingResponse: response,
                  responseId: response.id,

                  // IMPORTANTE: Passar os dados já carregados
                  existingAnswers: response.answers || {},
                  existingFindings: response.findings || {},
                  existingSavedFindings: response.savedFindings || {},
                  existingNotApplicableReasons: response.notApplicableReasons || {},
                  existingAttachments: response.attachments || {},

                  // Informações do avaliador original
                  originalAvaliador: response.avaliador,

                  // Garantir compatibilidade
                  currentProgress: {
                    answered: Object.keys(response.answers || {}).length,
                    total: formData.totalQuestions || 0,
                  },
                };

                console.log('✅ Formulário enriquecido para validação:', {
                  formId: enrichedForm.id,
                  formTitle: enrichedForm.title,
                  sectionsCount: enrichedForm.sections?.length || 0,
                  questionsCount: enrichedForm.questions?.length || 0,
                  requirementsCount: enrichedForm.requirements?.length || 0,
                  responseId: enrichedForm.responseId,
                  avaliadorOriginal: enrichedForm.originalAvaliador?.nome,
                  answersCount: Object.keys(enrichedForm.existingAnswers).length,
                  attachmentsCount: Object.keys(enrichedForm.existingAttachments).length,
                  totalQuestions: enrichedForm.totalQuestions,
                });

                // Verificar se realmente temos dados antes de prosseguir
                if (
                  !enrichedForm.id ||
                  (!enrichedForm.sections?.length &&
                    !enrichedForm.questions?.length &&
                    !enrichedForm.requirements?.length)
                ) {
                  console.error('❌ ERRO CRÍTICO: Formulário sem dados estruturais');
                  Alert.alert('Erro', 'Estrutura do formulário não encontrada');
                  return;
                }

                // Chamar a função onEditResponse com o formulário enriquecido
                onEditResponse(enrichedForm);
              } catch (error) {
                console.error('❌ Erro ao carregar formulário para validação:', error);
                Alert.alert(
                  'Erro',
                  'Não foi possível carregar o formulário para edição. Por favor, tente novamente.'
                );
              }
            }}
            icon="clipboard-check-outline"
            style={styles.viewButton}
          >
            Revisar e Validar
          </Button>

          {isPending ? (
            <Button
              mode="contained"
              onPress={() => handleValidate(response)}
              loading={isValidating}
              disabled={isValidating || !viewedResponses.has(response.id)}
              icon="check-circle"
              buttonColor={viewedResponses.has(response.id) ? '#4CAF50' : '#CCCCCC'}
              style={styles.validateButton}
            >
              {!viewedResponses.has(response.id) ? (
                <Text style={styles.alertText}>⚠️ Visualize primeiro</Text>
              ) : (
                'Validar'
              )}
            </Button>
          ) : (
            <View style={styles.validatedSection}>
              <View style={styles.validationBadge}>
                <Text style={styles.validationBadgeIcon}>✅</Text>
                <View>
                  <Text style={styles.validationBadgeText}>Validado</Text>
                  <Text style={styles.validationBadgeDate}>
                    {response.validacao?.dataValidacao
                      ? formatDate(response.validacao.dataValidacao)
                      : response.completedAt
                        ? formatDate(response.completedAt)
                        : 'Data não disponível'}
                  </Text>
                </View>
              </View>

              <Button
                mode="text"
                onPress={() => handleValidate(response)}
                loading={isValidating}
                disabled={isValidating || !viewedForRevalidation.has(response.id)}
                icon="refresh"
                compact
                textColor={viewedForRevalidation.has(response.id) ? '#2196F3' : '#CCCCCC'}
                style={styles.revalidateButton}
              >
                {!viewedForRevalidation.has(response.id) ? '⚠️' : '🔄'} Revalidar
              </Button>
            </View>
          )}
        </Card.Actions>
      </Card>
    );
  };

  // Aplicar filtros e ordenação
  const filteredPending = sortResponses(filterResponses(pendingResponses));
  const filteredValidated = sortResponses(filterResponses(validatedResponses));

  if (loading) {
    return (
      <View style={styles.container}>
        <Appbar.Header style={styles.appbar}>
          <Appbar.BackAction onPress={onBack} color={AVAQualisTheme.colors.onPrimary} />
          <Appbar.Content title="Validação da Coordenação" titleStyle={styles.appbarTitle} />
        </Appbar.Header>

        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={AVAQualisTheme.colors.primary} />
          <Text style={styles.loadingText}>Carregando respostas...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar}>
        <Appbar.BackAction onPress={onBack} color={AVAQualisTheme.colors.onPrimary} />
        <Appbar.Content
          title="Validação da Coordenação"
          subtitle={`${filteredPending.length} pendente(s) • ${filteredValidated.length} validada(s)`}
          titleStyle={styles.appbarTitle}
          subtitleStyle={styles.appbarSubtitle}
        />
        <Menu
          visible={menuVisible}
          onDismiss={() => setMenuVisible(false)}
          anchor={
            <Appbar.Action
              icon="sort"
              onPress={() => setMenuVisible(true)}
              color={AVAQualisTheme.colors.onPrimary}
            />
          }
        >
          <Menu.Item
            onPress={() => {
              setSortBy('date');
              setMenuVisible(false);
            }}
            title="Ordenar por Data"
            leadingIcon={sortBy === 'date' ? 'check' : undefined}
          />
          <Menu.Item
            onPress={() => {
              setSortBy('name');
              setMenuVisible(false);
            }}
            title="Ordenar por Nome"
            leadingIcon={sortBy === 'name' ? 'check' : undefined}
          />
        </Menu>
      </Appbar.Header>

      {/* Barra de busca */}
      <View style={styles.searchContainer}>
        <Searchbar
          placeholder="Buscar por título, avaliador, processo..."
          onChangeText={setSearchQuery}
          value={searchQuery}
          style={styles.searchBar}
          iconColor={AVAQualisTheme.colors.primary}
        />
      </View>

      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              loadResponses();
            }}
          />
        }
      >
        {/* Seção Pendentes */}
        {filteredPending.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>⏳ Aguardando Validação</Text>
              <Badge style={styles.sectionBadge}>{filteredPending.length}</Badge>
            </View>

            <ValidationInstructions isValidated={false} />

            {filteredPending.map(response => renderResponseCard(response, true))}
          </View>
        )}
        {/* Seção Validadas */}
        {filteredValidated.length > 0 && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>✅ Validadas</Text>
              <Badge style={[styles.sectionBadge, styles.validatedBadge]}>
                {filteredValidated.length}
              </Badge>
            </View>

            <ValidationInstructions isValidated={true} />

            {filteredValidated.map(response => renderResponseCard(response, false))}
          </View>
        )}

        {/* Mensagem vazia */}
        {filteredPending.length === 0 && filteredValidated.length === 0 && (
          <Card style={styles.emptyCard}>
            <Card.Content>
              <View style={styles.emptyContent}>
                <MaterialCommunityIcons name="clipboard-check-outline" size={64} color="#CCCCCC" />
                <Title style={styles.emptyTitle}>Nenhuma resposta encontrada</Title>
                <Paragraph style={styles.emptyText}>
                  {searchQuery
                    ? 'Tente ajustar os filtros de busca'
                    : 'Não há respostas completas para validar no momento'}
                </Paragraph>
              </View>
            </Card.Content>
          </Card>
        )}

        <View style={styles.bottomSpacer} />
      </ScrollView>
    </View>
  );
}
